// /pricing — Cennik w języku minimal (sesja 38, artboardy 01a–01d).
//
// JEDNA kolumna 1080 px, pięć sekcji jedna pod drugą: nagłówek z pigułką stanu
// sprzedaży, „Ile to kosztuje”, „Plany” (z darmowym startem pod nimi), „Pakiety
// tokenów”, „Zanim zapłacisz”, i linia prawna na końcu. Jedyny akcent strony to
// obrys karty polecanej (decyzja 9 MAPY) — bez plakietki, bez gradientu.
//
// REGUŁA 34 (runda B-R3, decyzja Domiego 20.09). Walutą produktu jest TOKEN.
// Przeliczenia („500 gemów = 166 editów”, „po 3 za edit”) znikają razem ze słowem:
// przy 1 tokenie za edit zdanie „500 tokenów = 500 editów” niczego nie dodaje, a
// przy innej stawce mówiłoby to, co i tak stoi w „Ile to kosztuje”. Nazwy pól API
// (`gem_costs`, `gem_packs_by_plan`) to adresy techniczne i zostają.
//
// CO WYLECIAŁO przez zasadę 8 i przez MAPĘ: akapit i podtytuł hero, „Ta sama cena
// w każdym planie.”, tabela „Czym się różnią”, pasek zalogowanego z saldem, karty
// zaufania z tytułem i akapitem (zostały trzy fakty jednozdaniowe), zdanie
// „Ważne 24 miesiące…” (została etykieta przy nagłówku sekcji).
//
// EVERY number on this page comes from GET /api/billing/plans — prices, token
// allowances, account slots, storage, the action costs and the add-on matrix.
// Nothing here is typed in: a hardcoded price on the page a customer decides
// from, next to a different price at the checkout, is what a payment processor
// treats as a red flag and a customer treats as a lie.
//
// GATE 3 jest STANEM STRONY: przy `available === false` pigułka stoi przy tytule,
// a każdy przycisk zakupu jest szary — nigdy ukryty i nigdy ze zdaniem powodu
// (zasada 8, punkt 2).

import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { useAuthOptional } from '../auth/AuthContext'
import { useI18n, useT } from '../lib/i18n'
import { usePublicPath } from '../lib/publicPaths'
// Grupowanie tysiecy: WSPOLNE (paczka P1 B). Strona miala wlasne `count()` ze zwykla
// spacja, wiec „4 000" wolno bylo zlamac na pol - na stronie, na ktorej klient decyduje
// sie zaplacic. Teraz grupuje `locales/format.groupCount`, wolane przez `TokenAmount` (paczka
// P1 C), wiec znak i szerokosc przerwy sa te same co w calym produkcie.
import { price } from '../lib/money'
import { editWord, pluralForm } from '../locales/plural'
import { FREE_START_EDITS } from '../lib/startBonus'
import Seo from '../components/Seo'
import Icon from '../components/Icon'
import { usePlans } from '../hooks/useBilling'
import { useGone } from '../hooks/useGone'
import type { BillingPlan, GemPack, PlansResponse } from '../lib/types'
import { ApiError } from '../lib/api'
import { Button, Glyph, StatusPill, TokenAmount } from '../ds'
import PublicHeader from '../components/public/PublicHeader'
import PublicFooterBar from '../components/public/PublicFooterBar'
import '../styles/public.css'
import '../styles/minimal-money.css'

type T = ReturnType<typeof useT>
type Locale = 'pl' | 'en'

/** Liczba w kroju mono — jedyny sposób, w jaki liczby stoją na tej stronie. */
function Num({ children }: { children: React.ReactNode }) {
  return <span className="mn-num-mono">{children}</span>
}

const usable = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0

export default function Pricing() {
  const t = useT()
  const { locale } = useI18n()
  const pub = usePublicPath()
  const plans = usePlans()
  const data = plans.data
  // Pasek zalogowanego z saldem WYLECIAŁ (zasada 8) — konto zostaje tu wyłącznie
  // po to, żeby przycisk prowadził tam, gdzie ma prowadzić: gość rejestruje się
  // najpierw, bo `/order` jest za logowaniem. `useAuthOptional`, bo ta strona
  // renderuje się także bez providera (prerender, testy).
  const signedIn = !!useAuthOptional()?.user
  // GATE 3 (0019, R4): tylko literalne `true` z BIEŻĄCEGO odczytu otwiera zakup.
  // „Nie wiem” — ładowanie, błąd, starszy serwer — jest zamknięte, bo na ścieżce
  // pieniędzy bezpieczny domyślny stan to ten, który nikogo nie obciąża.
  const open = !plans.loading && !plans.error && data?.available === true
  const paused = data?.available === false
  // JEDNA wypełniona akcja na ekranie (JEZYK 4, 57; FB-14 dla strony głównej i FAQ): dopóki
  // blok darmowego startu jest na ekranie, „Zacznij za darmo” nagłówka ma obrys (PROD2 F2-14).
  // Blok pojawia się dopiero z katalogiem, więc obserwowany element przychodzi przez stan —
  // nowy obiekt ref podpina obserwatora `useGone` od nowa, gdy blok wchodzi do drzewa.
  const [freeStart, setFreeStart] = useState<HTMLDivElement | null>(null)
  const freeStartRef = useMemo(() => ({ current: freeStart }), [freeStart])
  const freeStartGone = useGone(freeStartRef, true)

  return (
    <div className="public-page pricing-page">
      <Seo page="pricing" />
      <PublicHeader active="pricing" quietCta={freeStart != null && !freeStartGone} />

      <div className="mn-price">
        <div className="mn-price-head">
          <h1>{t('pricing.hero.titleOne')}</h1>
          {paused && <StatusPill tone="muted" data-testid="pricing-paused">{t('pricing.salesPaused')}</StatusPill>}
        </div>

        {/* Błąd zajmuje CAŁE pole pod nagłówkiem (01d): jedna oś, glif, zdanie,
            droga naprawy, kod. Sekcje, które przeżyły awarię katalogu, i tak nie
            mają o czym mówić, dopóki nie wiadomo, co jest w sprzedaży. */}
        {plans.error ? (
          <PricingError error={plans.error} onRetry={plans.refetch} t={t} />
        ) : (
          <>
            {!data ? <PricingSkeleton t={t} /> : (
              <>
                <GemCosts data={data} locale={locale} t={t} />
                <Plans data={data} locale={locale} open={open} signedIn={signedIn} t={t} freeStartRef={setFreeStart} />
                <Addons data={data} locale={locale} open={open} signedIn={signedIn} t={t} />
              </>
            )}
            <Trust t={t} />
            <div className="mn-legal" data-testid="pricing-legal">
              <p>{t('pricing.trust.sellerLine')}</p>
              <div className="mn-legal-line">
                <span>{t('pricing.grossNote')}</span>
                <span>{t('checkout.priceChangeNote')}</span>
                <Link className="mn-lnk" to={pub('/zwroty')}>{t('pricing.trust.refundsShort')}</Link>
              </div>
            </div>
          </>
        )}
      </div>

      <PublicFooterBar />
    </div>
  )
}

/** Błąd katalogu: glif 64, zdanie, droga naprawy, kod dla pomocy (01d). */
function PricingError({ error, onRetry, t }: { error: Error; onRetry: () => void; t: T }) {
  return (
    <div className="mn-empty mn-empty--money" role="alert" data-testid="pricing-error">
      <Glyph name="token" size={64} tone="current" />
      <h2 className="mn-empty-title">{t('pricing.state.error')}</h2>
      <div className="mn-empty-action">
        <button type="button" className="mn-lnk" onClick={onRetry} data-testid="pricing-retry">
          <Icon name="RefreshCw" size={18} /> {t('pricing.state.retry')}
        </button>
      </div>
      <div className="mn-empty-code mono">{`pricing-${error instanceof ApiError ? error.status : 'unavailable'}`}</div>
    </div>
  )
}

/**
 * Szkielet (01c) ma KSZTAŁT gotowej treści i ten sam rytm sekcji: trzy komórki
 * kosztów, trzy karty planów z obrysem akcentu na środkowej, trzy wiersze
 * pakietów. Sekcje, które nie zależą od API („Zanim zapłacisz”, linia prawna),
 * stoją od razu prawdziwe — nie ma po co mrugać tekstem, który już jest.
 */
function PricingSkeleton({ t }: { t: T }) {
  return (
    <>
      <section className="mn-price-sec" aria-busy="true" data-testid="pricing-skeleton">
        <h2>{t('pricing.gemCost.heading')}</h2>
        <div className="mn-gc">
          {[0, 1, 2].map((i) => (
            <div key={i}>
              <span className="mn-money-sk mn-money-sk--line mn-money-sk--w60" />
              <span className="mn-money-sk mn-money-sk--pill" />
            </div>
          ))}
        </div>
      </section>
      <section className="mn-price-sec" id="plans" aria-busy="true">
        <h2>{t('pricing.plans.heading')}</h2>
        <div className="mn-plans">
          {[0, 1, 2].map((i) => (
            <div className={`mn-plan${i === 1 ? ' is-featured is-shown' : ''}`} key={i}>
              <span className="mn-money-sk mn-money-sk--line mn-money-sk--w40" />
              <span className="mn-money-sk mn-money-sk--price" />
              <div className="mn-sk-stack">
                {[0, 1, 2].map((r) => <span className="mn-money-sk mn-money-sk--line" key={r} />)}
              </div>
              <span className="mn-money-sk mn-money-sk--btn mn-sk-push" />
            </div>
          ))}
        </div>
      </section>
      <section className="mn-price-sec" id="topups" aria-busy="true">
        <h2>{t('pricing.addons.headingShort')}<span className="mn-price-lab">{t('pricing.addons.validForLabel')}</span></h2>
        <div className="mn-addons">
          {[0, 1, 2].map((i) => (
            <div className="mn-addon" key={i}>
              <span className="mn-money-sk mn-money-sk--line" />
              <span className="mn-money-sk mn-money-sk--line mn-money-sk--w60" />
              <span className="mn-money-sk mn-money-sk--line mn-money-sk--w60" />
              <span className="mn-money-sk mn-money-sk--btn mn-money-sk--sbt" />
            </div>
          ))}
        </div>
      </section>
    </>
  )
}

/**
 * Ile to kosztuje — trzy stawki z odpowiedzi API, zanim padnie nazwa
 * jakiegokolwiek planu: „500 tokenów” nic nie znaczy, dopóki nie wiadomo, ile
 * tokenów kosztuje edit, publikacja i pocięcie materiału.
 *
 * Podpisy stawek („do 30 sekund, 1080p”) wyleciały przez zasadę 8: nazwa czynności
 * i liczba mówią wszystko, a reszta była opisem produktu w tabeli cen.
 */
function GemCosts({ data, locale, t }: { data: PlansResponse; locale: Locale; t: T }) {
  // Katalog może przyjść BEZ `gem_costs` (starszy serwer, niepełna odpowiedź).
  // To znaczy „nie wiemy, ile kosztuje czynność” — mówimy to wprost, nigdy
  // TypeError, który gasi stronę, i nigdy liczba wpisana tutaj.
  const costs = data.gem_costs ?? ({} as Partial<PlansResponse['gem_costs']>)
  const items = [
    { n: costs.render, label: t('pricing.gemCost.render') },
    { n: costs.publish, label: t('pricing.gemCost.publish') },
    { n: costs.cut, label: t('pricing.gemCost.cut') },
  ]
  return (
    <section className="mn-price-sec">
      <h2>{t('pricing.gemCost.heading')}</h2>
      {items.every((it) => usable(it.n)) ? (
        <div className="mn-gc" data-testid="gem-costs">
          {items.map((it) => (
            <div key={it.label}>
              <b>{it.label}</b>
              {/* N11 (P1-04, reguła 48): koszt czynności to DANA - glif i liczba, jak pula planu
                  niżej; odmienione słowo słyszy czytnik. Klasa mono niesie stopień 26 px. */}
              <span className="mn-gc-v"><TokenAmount locale={locale} n={it.n as number} place="pack" className="mn-num-mono" /></span>
            </div>
          ))}
        </div>
      ) : (
        <p className="mn-t-meta" data-testid="gem-costs-unavailable">{t('pricing.gemCost.unavailable')}</p>
      )}
    </section>
  )
}

/**
 * Plany. Na 1440 trzy karty obok siebie; na 390 zakładki z nazwą I CENĄ, a pod
 * nimi JEDNA karta — przełącznik pokazujący same nazwy kazałby otwierać każdą
 * kartę po to, żeby poznać cenę.
 */
function Plans({ data, locale, open, signedIn, t, freeStartRef }: {
  data: PlansResponse; locale: Locale; open: boolean; signedIn: boolean; t: T
  freeStartRef?: (el: HTMLDivElement | null) => void
}) {
  // Plan bez podpiętej ceny w Stripe NIE jest rysowany (canvas 3, „Stany” 05).
  // Nie jako „wkrótce”: plan bez ceny to obietnica bez pokrycia i dokładnie to,
  // co wyłapuje przegląd PSP.
  const list = data.plans.filter((p) => p.purchasable)
  const featured = list.length >= 3 ? Math.floor(list.length / 2) : 0
  const [selected, setSelected] = useState<string | null>(null)
  const current = selected ?? list[featured]?.code ?? null
  if (list.length === 0) return null

  return (
    <section className="mn-price-sec" id="plans">
      <h2>{t('pricing.plans.heading')}</h2>
      <div className="mn-ptabs" role="group" aria-label={t('pricing.switch.label')} data-testid="plan-switch">
        {list.map((plan) => (
          <button
            key={plan.code}
            type="button"
            className={plan.code === current ? 'is-active' : ''}
            aria-pressed={plan.code === current}
            onClick={() => setSelected(plan.code)}
          >
            {plan.name}
            <small>{price(plan.price_cents, data.currency, locale)}</small>
          </button>
        ))}
      </div>
      <div className="mn-plans">
        {list.map((plan, i) => (
          <PlanCard key={plan.code} plan={plan} data={data} locale={locale} open={open} signedIn={signedIn}
                    t={t} featured={i === featured} shown={plan.code === current} />
        ))}
      </div>
      <FreeStart open={open} signedIn={signedIn} locale={locale} t={t} anchor={freeStartRef} />
    </section>
  )
}

/**
 * Darmowy start pod planami (38-R3).
 *
 * Działa NIEZALEŻNIE od sprzedaży — konto zakłada się bez płatności — więc przy
 * WSTRZYMANEJ sprzedaży jest jedyną żywą akcją tej strony i dostaje wypełniony
 * przycisk; przy otwartej schodzi do małego cichego przycisku, żeby nie
 * konkurował z „Wybierz Pro”. Mikrotekst („Bez karty · {n} editów gratis”) to samo zdanie co
 * mikrotekst strony głównej i FAQ; liczbę podaje FREE_START_EDITS (lib/startBonus.ts), więc
 * NICZEGO nie przelicza na tokeny i nie rozjedzie się z backendem.
 *
 * Zalogowany nie widzi tego bloku: „zacznij za darmo” dla konta, które już
 * istnieje, prowadzi donikąd.
 */
function FreeStart({ open, signedIn, locale, t, anchor }: {
  open: boolean; signedIn: boolean; locale: Locale; t: T; anchor?: (el: HTMLDivElement | null) => void
}) {
  if (signedIn) return null
  return (
    <div className="mn-free" data-testid="pricing-free-start" ref={anchor}>
      <Button variant={open ? 'quiet' : 'primary'} className={open ? 'mn-free-quiet' : undefined} to="/register">
        {t('pricing.freeStart.cta')}
      </Button>
      <span>{t('pricing.guarantee.freeEdits', { n: FREE_START_EDITS, edits: editWord(locale, FREE_START_EDITS) })}</span>
    </div>
  )
}

function PlanCard({ plan, data, locale, open, signedIn, featured, shown, t }: {
  plan: BillingPlan; data: PlansResponse; locale: Locale; open: boolean; signedIn: boolean
  featured: boolean; shown: boolean; t: T
}) {
  return (
    <div className={['mn-plan', featured && 'is-featured', shown && 'is-shown'].filter(Boolean).join(' ')}
         data-plan={plan.code}>
      <h3>{plan.name}</h3>
      <div className="mn-plan-price">
        <b className="mn-num-second">{price(plan.price_cents, data.currency, locale)}</b>
        {t('pricing.plans.perMonth')}
      </div>
      <ul>
        {/* Pula TAKA, JAKĄ ODDAJE API — bez dzielenia jej przez cokolwiek.
            „= N editów” zniknęło razem z regułą 34: przy jednym tokenie za edit
            to była ta sama liczba dwa razy. */}
        {/* Paczka P1 C: pula jest DANĄ — moneta i liczba, bez słowa. Słowo zostaje
            w ZDANIU przelicznika niżej („1 edit = 1 token…”), bo tam jest zdaniem. */}
        <li className="mn-plan-eq">
          <TokenAmount locale={locale} n={plan.tokens_per_period} place="plan" />
          {' '}{t('pricing.plans.poolPerMonth')}
        </li>
        <li><Num>{plan.slots_included}</Num> {t(`pricing.plans.accounts_${pluralForm(locale, plan.slots_included)}`)}</li>
        {plan.storage_gb !== null && (
          <li><Num>{plan.storage_gb}</Num> GB {t('pricing.plans.storage')}</li>
        )}
      </ul>
      {/* Gość rejestruje się najpierw; zalogowany idzie prosto do Zamówienia,
          gdzie obie zgody padają, zanim powstanie jakakolwiek sesja Stripe.
          Przy wstrzymanej sprzedaży przycisk zostaje WIDOCZNY i szary. */}
      {open
        ? <Button variant={featured ? 'primary' : 'secondary'} block to={signedIn ? `/order?plan=${plan.code}` : '/register'} data-testid={`plan-cta-${plan.code}`}>
            {t('pricing.plans.cta', { plan: plan.name })}
          </Button>
        : <Button variant="secondary" block disabled data-testid={`plan-cta-${plan.code}`}>
            {t('pricing.plans.cta', { plan: plan.name })}
          </Button>}
    </div>
  )
}

/**
 * Pakiety gemów jako WIERSZE, nie kafle: klient, któremu zabrakło w połowie
 * miesiąca, szuka jednej liczby i jednej ceny, a nie porównania.
 *
 * Pakiet spoza planu ma swój wiersz i swoją cenę, a jego akcja jest szara —
 * pigułka „tylko plan Max” mówi dlaczego, więc drugie zdanie byłoby tłumaczeniem
 * kontrolki (zasada 8, punkt 2).
 */
function Addons({ data, locale, open, signedIn, t }: {
  data: PlansResponse; locale: Locale; open: boolean; signedIn: boolean; t: T
}) {
  // Każdy pakiet, jaki istnieje na dowolnym poziomie, z lepszą stawką Maxa
  // dopisaną tam, gdzie ten sam pakiet kosztuje na nim mniej.
  const byTokens = new Map<number, { standard?: GemPack; best?: GemPack; maxOnly: boolean }>()
  for (const [code, packs] of Object.entries(data.gem_packs_by_plan)) {
    for (const pack of packs) {
      const entry = byTokens.get(pack.tokens) ?? { maxOnly: true }
      if (code === 'none') continue
      if (code === 'max') entry.best = pack
      else { entry.standard = pack; entry.maxOnly = false }
      byTokens.set(pack.tokens, entry)
    }
  }
  const rows = [...byTokens.entries()].sort((a, b) => a[0] - b[0])
  if (rows.length === 0) return null

  return (
    <section className="mn-price-sec" id="topups">
      <h2>{t('pricing.addons.headingShort')}<span className="mn-price-lab">{t('pricing.addons.validForLabel')}</span></h2>
      <div className="mn-addons">
        {rows.map(([tokens, e]) => {
          const headline = e.standard ?? e.best
          if (!headline) return null
          const note = e.maxOnly
            ? t('pricing.addons.maxOnly')
            : e.standard && e.best && e.best.price_cents !== e.standard.price_cents
              ? t('pricing.addons.maxPrice', { price: price(e.best.price_cents, data.currency, locale) })
              : null
          const off = !open || e.maxOnly
          return (
            <div className="mn-addon" key={`gems-${tokens}`} data-testid={`pack-${tokens}`}>
              <b className="mn-addon-name"><TokenAmount locale={locale} n={tokens} place="pack" /></b>
              <small>{note}</small>
              <span className="mn-addon-price">{price(headline.price_cents, data.currency, locale)}</span>
              {/* Reguła 28: akcja wiersza to MAŁY przycisk 32 px przy prawej
                  krawędzi kolumny, nie link rozciągnięty na resztę wiersza. */}
              {off
                ? <Button variant="secondary" className="mn-sbt" disabled>{t('pricing.addons.buy')}</Button>
                : <Button variant="secondary" className="mn-sbt" to={signedIn ? `/order?gems=${tokens}` : '/register'}>{t('pricing.addons.buy')}</Button>}
            </div>
          )
        })}
      </div>
    </section>
  )
}

/**
 * Trzy rzeczy, o które klient pyta PRZED zapłatą, każda jednym zdaniem.
 * Deskryptor karty stoi tu świadomie: nierozpoznane obciążenie na wyciągu
 * kończy się chargebackiem, a chargebacki kosztują młode konto handlowe jego
 * możliwość przyjmowania płatności.
 */
function Trust({ t }: { t: T }) {
  const facts = [t('pricing.trust.factCancel'), t('pricing.trust.factDescriptor'), t('pricing.trust.factRefund')]
  return (
    <section className="mn-price-sec">
      <h2>{t('pricing.trust.heading')}</h2>
      <div className="mn-facts" data-testid="pricing-trust">
        {facts.map((fact) => (
          <p key={fact}><Icon name="Check" size={18} /><span>{fact}</span></p>
        ))}
      </div>
    </section>
  )
}
